import {
  Stack,
  TextField,
  PasswordField,
  Typography,
  CheckboxWithLabel,
  Box,
  Link,
  LoginButton,
  InlineNotification,
} from '@packages/shared';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { zodResolver } from '@hookform/resolvers/zod';
import { ButtonBase } from '@mui/material';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import * as zod from 'zod';
import type {
  GTMEventGlycerinDisplayForm,
  GTMEventGlycerinDisplayError,
} from '@packages/tracking/src/types/events';
import { GlycerinErrorSeverity } from '@packages/tracking/src/types/enums';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { PasswordForgotDialog } from './PasswordForgotDialog';

const messages = defineMessages({
  emailLabel: {
    id: 'Login.email.label',
    defaultMessage: 'E-Mail-Adresse',
  },
  emailPlaceholder: {
    id: 'Login.email.placeholder',
    defaultMessage: 'name@provider.de',
  },
  ariaLabelEmail: {
    id: 'Login.email.aria-label',
    defaultMessage: 'email-eingabe',
  },
  passwordLabel: {
    id: 'Login.password.label',
    defaultMessage: 'Passwort',
  },
  passwordPlaceholder: {
    id: 'Login.password.placeholder',
    defaultMessage: '(mind. 6 Zeichen)',
  },
  ariaLabelPassword: {
    id: 'Login.password.aria-label',
    defaultMessage: 'passwort-eingabe',
  },
  stayLoggedIn: {
    id: 'Login.label.stay-logged-in',
    defaultMessage: 'Auf diesem Gerät angemeldet bleiben',
  },
  forgotPasswordLabel: {
    id: 'Login.label.forgot-password',
    defaultMessage: 'Passwort vergessen?',
  },
  showPasswordButtonLabelShow: {
    id: 'Login.label.show-password',
    defaultMessage: 'anzeigen',
  },
  showPasswordButtonLabelHide: {
    id: 'Login.label.hide-password',
    defaultMessage: 'ausblenden',
  },
  passwordCounterLabel: {
    id: 'Login.label.password-counter',
    defaultMessage: 'Zeichen',
  },
  errorInvalidMail: {
    id: 'Login.message.invalidMail',
    defaultMessage: 'Die E-Mail-Adresse ist nicht gültig.',
  },
  errorMandatoryField: {
    id: 'PWLogin.message.mandatoryField',
    defaultMessage: 'Das ist ein Pflichtfeld.',
  },
  errorTooShort: {
    id: 'PWLogin.message.tooShort',
    defaultMessage: 'Das Passwort ist zu kurz.',
  },
  errorTooLong: {
    id: 'PWLogin.message.tooLong',
    defaultMessage: 'Das Passwort ist zu lang.',
  },
});

interface PasswordLoginValues {
  email: string;
  password: string;
  stayLoggedIn?: boolean;
}

export interface PasswordLoginFormProps {
  /** Invokes when validation passed and form submitted */
  onFormSubmit: (values: PasswordLoginValues) => void;
  /** custom error message (e.g. from backend) */
  error?: string | null;
}

/** E-Mail and password login form with forgot password dialog */
export const PasswordLoginForm = ({ onFormSubmit, error }: PasswordLoginFormProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { formatMessage } = useIntl();
  const dispatchGTMEvent = useTracking();
  const { locale } = useI18n();
  const config = useConfig();

  useEffect(() => {
    dispatchGTMEvent<GTMEventGlycerinDisplayForm>({
      event: 'DisplayForm',
      DisplayFormData: {
        category: 'email-login-form',
      },
    });
  }, [dispatchGTMEvent]);

  const schema = useMemo(
    () =>
      zod.object({
        email: zod
          .string()
          .nonempty(formatMessage(messages.errorMandatoryField))
          .email(formatMessage(messages.errorInvalidMail)),
        password: zod
          .string()
          .nonempty(formatMessage(messages.errorMandatoryField))
          .min(6, formatMessage(messages.errorTooShort))
          .max(256, formatMessage(messages.errorTooLong)),
        stayLoggedIn: zod.boolean().optional(),
      }),
    [formatMessage],
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = useForm<PasswordLoginValues>({
    defaultValues: {
      password: '',
      stayLoggedIn: true,
    },
    resolver: zodResolver(schema),
    mode: 'onBlur',
  });
  useEffect(() => {
    // why Set and not array? because we want to avoid duplicate events in our loop caused from human error
    const errorKeys = new Set<keyof PasswordLoginValues>([
      'email',
      'password',
      'stayLoggedIn',
    ] satisfies Array<keyof PasswordLoginValues>);

    errorKeys.forEach((key) => {
      if (errors[key]) {
        dispatchGTMEvent<GTMEventGlycerinDisplayError>({
          event: 'DisplayError',
          DisplayErrorData: {
            category: 'email-login-form',
            errors: [
              {
                errorType: 'formValidation',
                message: errors[key]?.message ?? '',
                severity: GlycerinErrorSeverity.WARNING,
                source: 'zodResolver',
              },
            ],
          },
        });
      }
    });
    // dependencies need to be deeper than just errors as useeffect dependency doesn't do deep object changes, otherwise the desired effect is not triggered
  }, [
    errors.email?.message,
    errors.password?.message,
    errors.stayLoggedIn?.message,
    errors,
    dispatchGTMEvent,
  ]);
  const handleOpenDialog = () => setDialogOpen(true);
  const handleCloseDialog = () => setDialogOpen(false);

  return (
    <>
      {/* conditional render to not keep mounted */}
      {dialogOpen && <PasswordForgotDialog isOpen onClose={handleCloseDialog} />}
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Stack
          sx={{
            alignItems: 'flex-start',
            mb: 2,
            p: 3,
            boxShadow: 1,
            bgcolor: 'common.white',
            borderRadius: '3px',
          }}
          spacing={2}
        >
          {Boolean(error) && (
            <InlineNotification severity="error">
              {formatMessage(
                { id: 'backend-error', defaultMessage: `${error}` },
                {
                  maillink: (parts: ReactNode) => <a href={`mailto:${parts}`}>{parts}</a>,
                  migrate: (parts: ReactNode) => (
                    <Link
                      href={`${getLocalizedPath(config.order.paths.myAccountOverview, locale)}?toMigrate=true`}
                      sx={{ color: 'text.dark', textDecoration: 'underline' }}
                    >
                      {parts}
                    </Link>
                  ),
                },
              )}
            </InlineNotification>
          )}
          <TextField
            {...register('email')}
            label={formatMessage(messages.emailLabel)}
            placeholder={formatMessage(messages.emailPlaceholder)}
            error={!!errors.email}
            helperText={errors.email ? errors.email?.message : undefined}
            fullWidth
            inputProps={{
              'data-testid': 'searchfield-input',
              'aria-label': formatMessage(messages.ariaLabelEmail),
            }}
          />
          <Controller
            control={control}
            name="password"
            render={({ field: { onChange, onBlur, value } }) => (
              <PasswordField
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                label={formatMessage(messages.passwordLabel)}
                placeholder={formatMessage(messages.passwordPlaceholder)}
                error={!!errors.password}
                helperText={errors.password ? errors.password?.message : undefined}
                showPasswordButtonLabelShow={formatMessage(messages.showPasswordButtonLabelShow)}
                showPasswordButtonLabelHide={formatMessage(messages.showPasswordButtonLabelHide)}
                charsCounterLabel={formatMessage(messages.passwordCounterLabel)}
                fullWidth
                inputProps={{
                  'data-testid': 'searchfield-input',
                  'aria-label': formatMessage(messages.ariaLabelPassword),
                }}
              />
            )}
          />
          <ButtonBase onClick={handleOpenDialog} disableRipple>
            <Typography variant="body2" sx={{ textDecoration: 'underline' }}>
              {formatMessage(messages.forgotPasswordLabel)}
            </Typography>
          </ButtonBase>
          <Controller
            control={control}
            name="stayLoggedIn"
            render={({ field: { onChange, onBlur, value } }) => (
              <CheckboxWithLabel
                onChange={onChange}
                onBlur={onBlur}
                checked={!!value}
                label={formatMessage(messages.stayLoggedIn)}
              />
            )}
          />
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <LoginButton type="submit" />
        </Box>
      </form>
    </>
  );
};

// eslint-disable-next-line import-x/no-default-export
export default PasswordLoginForm;
